import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SkillAssessment } from '@/components/learningPaths/SkillAssessment';
import { 
  Brain, 
  Target, 
  Clock, 
  CheckCircle2,
  ArrowRight,
  BookOpen
} from 'lucide-react';
import { SkillAssessment as SkillAssessmentType, AssessmentQuestion } from '@/types/learningPathTypes';
import { ALL_LEARNING_TRACKS } from '@/data/learningTracks';

// Sample assessment data
const SKILL_ASSESSMENTS: SkillAssessmentType[] = [
  {
    id: 'programming-fundamentals',
    name: 'Programming Fundamentals Assessment',
    description: 'Test your basic programming knowledge and problem-solving skills',
    estimatedTime: '15 minutes',
    skillAreas: ['Programming Logic', 'Variables', 'Functions', 'Control Structures'],
    minimumScore: 60,
    questions: [
      {
        id: 'q1',
        question: 'What is a variable in programming?',
        type: 'multiple-choice',
        options: [
          'A fixed value that cannot be changed',
          'A container for storing data values',
          'A type of programming language',
          'A computer program'
        ],
        correctAnswer: 1,
        points: 10
      },
      {
        id: 'q2',
        question: 'Which of the following is a loop structure?',
        type: 'multiple-choice',
        options: [
          'if statement',
          'variable declaration',
          'for loop',
          'function definition'
        ],
        correctAnswer: 2,
        points: 10
      },
      {
        id: 'q3',
        question: 'What does this code output?\n\nlet x = 5;\nlet y = x + 3;\nconsole.log(y);',
        type: 'code',
        options: ['5', '8', '3', 'Error'],
        correctAnswer: 1,
        points: 15,
        codeTemplate: 'let x = 5;\nlet y = x + 3;\nconsole.log(y);'
      },
      {
        id: 'q4',
        question: 'What is the purpose of a function in programming?',
        type: 'multiple-choice',
        options: [
          'To store data permanently',
          'To create reusable blocks of code',
          'To display output to the user',
          'To declare variables'
        ],
        correctAnswer: 1,
        points: 10
      }
    ]
  },
  {
    id: 'web-development',
    name: 'Web Development Assessment',
    description: 'Evaluate your HTML, CSS, and JavaScript knowledge',
    estimatedTime: '20 minutes',
    skillAreas: ['HTML', 'CSS', 'JavaScript', 'Web Concepts'],
    minimumScore: 65,
    questions: [
      {
        id: 'w1',
        question: 'What does HTML stand for?',
        type: 'multiple-choice',
        options: [
          'Hypertext Markup Language',
          'High-level Text Markup Language',
          'Hypertext Modern Language',
          'Home Tool Markup Language'
        ],
        correctAnswer: 0,
        points: 10
      },
      {
        id: 'w2',
        question: 'Which CSS property is used to change the text color?',
        type: 'multiple-choice',
        options: ['background-color', 'color', 'text-style', 'font-color'],
        correctAnswer: 1,
        points: 10
      },
      {
        id: 'w3',
        question: 'How do you create a function in JavaScript?',
        type: 'multiple-choice',
        options: [
          'function = myFunction() {}',
          'create myFunction() {}',
          'function myFunction() {}',
          'def myFunction() {}'
        ],
        correctAnswer: 2,
        points: 15
      }
    ]
  }
];

export const SkillAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedAssessment, setSelectedAssessment] = useState<SkillAssessmentType | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [assessmentResults, setAssessmentResults] = useState<{
    score: number;
    recommendations: string[];
  } | null>(null);

  const handleAssessmentComplete = (score: number, recommendations: string[]) => {
    setAssessmentResults({ score, recommendations });
    setShowResults(true);
  };

  const getRecommendedTrack = (score: number): string => {
    if (score >= 80) {
      return 'fullstack'; // Advanced learners can handle full-stack
    } else if (score >= 60) {
      return 'frontend'; // Good foundation, start with frontend
    } else {
      return 'frontend'; // Start with basics
    }
  };

  const handleStartLearning = () => {
    if (assessmentResults) {
      const recommendedTrack = getRecommendedTrack(assessmentResults.score);
      navigate(`/learning-paths/${recommendedTrack}`);
    }
  };

  if (selectedAssessment && !showResults) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4">
          <SkillAssessment 
            assessment={selectedAssessment} 
            onComplete={handleAssessmentComplete}
          />
        </div>
      </div>
    );
  }

  if (showResults && assessmentResults) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                <Target className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">Your Personalized Learning Path</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center space-y-2">
                <div className="text-3xl font-bold text-primary">{assessmentResults.score}%</div>
                <p className="text-muted-foreground">Assessment Score</p>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold">Your Recommendations:</h4>
                <div className="space-y-2">
                  {assessmentResults.recommendations.map((rec, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-6 space-y-4">
                <h4 className="font-semibold">Recommended Learning Track:</h4>
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">🚀</span>
                    <span className="font-semibold">
                      {ALL_LEARNING_TRACKS.find(t => t.id === getRecommendedTrack(assessmentResults.score))?.name}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Based on your assessment results, this track will help you build the skills you need most.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-center pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSelectedAssessment(null);
                    setShowResults(false);
                    setAssessmentResults(null);
                  }}
                >
                  Take Another Assessment
                </Button>
                <Button onClick={handleStartLearning} className="gap-2">
                  Start Learning
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4 space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Brain className="w-8 h-8 text-primary" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Skill Assessment
            </h1>
          </div>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Discover your current skill level and get personalized learning recommendations 
            tailored to your experience and goals.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="text-center">
            <CardContent className="pt-6">
              <Target className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Personalized Path</h3>
              <p className="text-sm text-muted-foreground">
                Get learning recommendations based on your current skill level
              </p>
            </CardContent>
          </Card>
          
          <Card className="text-center">
            <CardContent className="pt-6">
              <Clock className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Quick & Easy</h3>
              <p className="text-sm text-muted-foreground">
                Complete assessments in 15-20 minutes and get instant results
              </p>
            </CardContent>
          </Card>
          
          <Card className="text-center">
            <CardContent className="pt-6">
              <BookOpen className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Skip What You Know</h3>
              <p className="text-sm text-muted-foreground">
                Avoid repeating topics you've already mastered
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Available Assessments */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-center">Choose Your Assessment</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {SKILL_ASSESSMENTS.map((assessment) => (
              <Card key={assessment.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{assessment.name}</CardTitle>
                      <p className="text-muted-foreground text-sm mt-2">
                        {assessment.description}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {assessment.estimatedTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <Target className="w-4 h-4" />
                      {assessment.questions.length} questions
                    </span>
                  </div>

                  <div className="space-y-2">
                    <span className="text-sm font-medium">Skill Areas:</span>
                    <div className="flex flex-wrap gap-1">
                      {assessment.skillAreas.map((skill) => (
                        <Badge key={skill} variant="secondary" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Button 
                    onClick={() => setSelectedAssessment(assessment)}
                    className="w-full gap-2"
                  >
                    Start Assessment
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20">
          <CardContent className="text-center py-8">
            <h3 className="text-2xl font-bold mb-4">Not Sure Where to Start?</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Take the Programming Fundamentals assessment to get a comprehensive evaluation 
              of your current skills and receive personalized learning recommendations.
            </p>
            <Button 
              size="lg" 
              onClick={() => setSelectedAssessment(SKILL_ASSESSMENTS[0])}
              className="gap-2"
            >
              Take General Assessment
              <Brain className="w-5 h-5" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};